/* eslint-disable no-undef */
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import conesImg from 'assets/enConstruccion.svg';
import targetIcon from 'assets/enConstruccionLista.svg';

// Clave secreta de acceso
const SECRET_CODE = 'akademia2026';

const SESSION_KEY = 'akademia_access_granted';

// Clicks necesarios en la esquina para revelar el campo de clave
const CLICKS_NEEDED = 3;
const CLICK_WINDOW_MS = 2000;

const hasAccess = () => sessionStorage.getItem(SESSION_KEY) === 'true';

const grantAccess = () => {
  sessionStorage.setItem(SESSION_KEY, 'true');
};

export const ConstructionGate = ({ children }) => {
  const [isUnlocked, setIsUnlocked] = useState(hasAccess());
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordError, setShowPasswordError] = useState(false);
  const clickTimestamps = useRef([]);

  const handleCornerClick = () => {
    const now = Date.now();
    clickTimestamps.current = [
      ...clickTimestamps.current.filter((t) => now - t < CLICK_WINDOW_MS),
      now,
    ];
    if (clickTimestamps.current.length >= CLICKS_NEEDED) {
      setShowPasswordField(true);
      clickTimestamps.current = [];
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === SECRET_CODE) {
      grantAccess();
      setIsUnlocked(true);
    } else {
      setShowPasswordError(true);
      setPassword('');
    }
  };

  // Mientras no haya acceso, el resto del front (children = toda la app,
  // incluyendo el <Switch> con todas las rutas) ni siquiera se monta.
  if (isUnlocked) {
    return children;
  }

  return (
    <PageWrap>
      {/* Zona invisible en la esquina: 3 clicks revelan el campo de clave */}
      <CornerTrigger onClick={handleCornerClick} aria-hidden="true" />

      <Card>
        <Brand>
          AKAD
          <BrandOrange>E</BrandOrange>
          MIA
        </Brand>

        <Subtitle>
          Estamos construyendo tu próxima experiencia de aprendizaje.
        </Subtitle>

        <ConesImage src={conesImg} alt="En construcción" />

        <InfoRow>
          <TargetIcon src={targetIcon} alt="" />
          <InfoBox>
            <InfoTitle>¿Qué somos?</InfoTitle>
            <InfoText>
              Somos plataforma multidisciplinaria donde los estudiantes
              enfrentan escenarios simulados que exigen análisis y toma de
              decisiones reales, en un entorno seguro y controlado.
            </InfoText>
          </InfoBox>
        </InfoRow>

        <Divider />

        <ContactText>¿Tienes preguntas? Escríbenos</ContactText>
        <ContactEmail href="mailto:contacto@akademia.com">
          ✉ contacto@akademia.com
        </ContactEmail>

        {showPasswordField && (
          <PasswordForm onSubmit={handlePasswordSubmit}>
            <PasswordInput
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setShowPasswordError(false);
              }}
              placeholder="Código de acceso"
              $error={showPasswordError}
              autoFocus
            />
            <EnterButton type="submit">Entrar</EnterButton>
          </PasswordForm>
        )}
      </Card>
    </PageWrap>
  );
};

const PageWrap = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  overflow-x: hidden;
`;

const CornerTrigger = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 60px;
  height: 60px;
  cursor: default;
  z-index: 10;
`;

const Card = styled.div`
  width: 100%;
  max-width: 900px;
  padding: 64px 56px;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 640px) {
    padding: 32px 20px;
  }
`;

const Brand = styled.p`
  font-size: 64px;
  font-weight: 700;
  color: #0C447C;
  margin: 0 0 36px;

  @media (max-width: 640px) {
    font-size: 36px;
    margin: 0 0 20px;
  }
`;

const BrandOrange = styled.span`
  color: #FF751F;
`;

const Subtitle = styled.p`
  font-size: 30px;
  color: #4a4a4a;
  line-height: 1.5;
  margin: 0 0 40px;

  @media (max-width: 640px) {
    font-size: 19px;
    margin: 0 0 24px;
  }
`;

const ConesImage = styled.img`
  width: 420px;
  max-width: 100%;
  height: auto;
  margin: 0 auto 44px;
  display: block;

  @media (max-width: 640px) {
    width: 220px;
    margin: 0 auto 28px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
  text-align: left;
  margin-bottom: 24px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
  }
`;

const TargetIcon = styled.img`
  width: 130px;
  height: 130px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 72px;
    height: 72px;
  }
`;

const InfoBox = styled.div`
  flex: 1;
  background: #e5e5e5;
  border-radius: 14px;
  padding: 16px 20px;
  box-sizing: border-box;
  width: 100%;
`;

const InfoTitle = styled.p`
  font-size: 22px;
  font-weight: 700;
  color: #0C447C;
  margin: 0 0 10px;

  @media (max-width: 640px) {
    font-size: 18px;
  }
`;

const InfoText = styled.p`
  font-size: 18px;
  color: #4a4a4a;
  line-height: 1.6;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 15px;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 24px 0;
`;

const ContactText = styled.p`
  font-size: 18px;
  color: #6a6a6a;
  margin: 0 0 10px;

  @media (max-width: 640px) {
    font-size: 15px;
  }
`;

const ContactEmail = styled.a`
  font-size: 18px;
  color: #0C447C;
  text-decoration: underline;
  font-weight: 600;

  @media (max-width: 640px) {
    font-size: 15px;
    word-break: break-word;
  }
`;

const PasswordForm = styled.form`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 32px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PasswordInput = styled.input`
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid ${({ $error }) => ($error ? '#e2504a' : '#d0d0d0')};
  font-size: 16px;
  font-family: 'Montserrat', sans-serif;
  outline: none;
  width: 260px;
  box-sizing: border-box;

  &:focus {
    border-color: #0C447C;
  }

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const EnterButton = styled.button`
  background: #FF751F;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 16px 28px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
`;
